import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import styled, { ThemeProvider, createGlobalStyle } from 'styled-components';
import HomePage from './pages/HomePage';
import EditPage from './pages/EditPage';
import SettingsPage from './pages/SettingsPage';
import AgentDashboard from './pages/AgentDashboard';
import Sidebar from './components/Sidebar';
import { ProjectProvider } from './context/ProjectContext';
import { SettingsProvider } from './context/SettingsContext';

const lightTheme = {
  background: '#ffffff',
  surface: '#f8f9fa',
  primary: '#007bff',
  secondary: '#6c757d',
  text: '#212529',
  textSecondary: '#6c757d',
  border: '#dee2e6',
  success: '#28a745',
  warning: '#ffc107',
  danger: '#dc3545',
  shadow: '0 2px 4px rgba(0,0,0,0.1)'
};

const darkTheme = {
  background: '#1a1a1a',
  surface: '#2d2d2d',
  primary: '#0d6efd',
  secondary: '#6c757d',
  text: '#ffffff',
  textSecondary: '#adb5bd',
  border: '#495057',
  success: '#198754',
  warning: '#ffc107',
  danger: '#dc3545',
  shadow: '0 2px 4px rgba(0,0,0,0.3)'
};

const GlobalStyle = createGlobalStyle`
  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
      'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
      sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${props => props.theme.background};
    color: ${props => props.theme.text};
    overflow: hidden;
  }

  code {
    font-family: source-code-pro, Menlo, Monaco, Consolas, 'Courier New',
      monospace;
  }

  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.surface};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.border};
    border-radius: 4px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.secondary};
  }

  /* Responsive adjustments */
  @media (max-width: 1200px) {
    body {
      font-size: 14px;
    }
  }

  @media (max-width: 900px) {
    body {
      font-size: 13px;
    }
  }
`;

const AppContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

function App() {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    // Load theme preference from localStorage
    const savedTheme = localStorage.getItem('beevids-theme');
    if (savedTheme) {
      setIsDarkMode(savedTheme === 'dark');
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkMode;
    setIsDarkMode(newTheme);
    localStorage.setItem('beevids-theme', newTheme ? 'dark' : 'light');
  };

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <GlobalStyle />
      <SettingsProvider>
        <ProjectProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <AppContainer>
              <Sidebar />
              <MainContent>
                <Routes>
                  <Route path="/" element={<Navigate to="/home" replace />} />
                  <Route path="/home" element={<HomePage />} />
                  <Route path="/edit/:projectId?" element={<EditPage />} />
                  <Route path="/settings" element={<SettingsPage toggleTheme={toggleTheme} isDarkMode={isDarkMode} />} />
                  <Route path="/agent-dashboard" element={<AgentDashboard />} />
                </Routes>
              </MainContent>
            </AppContainer>
          </Router>
        </ProjectProvider>
      </SettingsProvider>
    </ThemeProvider>
  );
}

export default App;